import type { ErrorInfo, ReactElement, ReactNode } from 'react';
import {
  Component,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { Anchor } from '@studio/design-system/button';
import { Loader } from '@studio/design-system/loader';
import { getCurrentVendorBadgeIcon, vendorName } from '@studio/design-system/themes/vendor.js';
import type { Agent } from '@studio/models/agent';
import type { Room, RoomRecord } from '@studio/models/room';
import { inspectInitialRoomInventory, listOrganizations, listRooms } from '#~/api/agent/index.js';
import type { BridgeSetupCapabilities } from '#~/api/agent/registry.js';
import { apiCode, setupErrorType } from './setup-error-type.js';
import { useMachineNames } from '#~/agent/hooks.js';
import { createBridgeLifecycleTracker } from '#~/analytics/domains/bridge-lifecycle.js';
import { createStudioBootstrapTracker } from '#~/analytics/domains/studio-bootstrap.js';
import { EVENT_NAMES, useAnalytics } from '#~/analytics/index.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { authStore, useAuthStore } from '#~/auth/store.js';
import { toOrganizationMap, toRoomRecordMap } from '#~/auth/registry-transformers.js';
import { useDepot } from '#~/depot/hooks.js';
import { useRenderedBootHumanWait } from '#~/routes/boot-overlay/boot-budget-view.js';
import { getBootSnapshot } from '#~/routes/root/boot-budget.js';
import { ElectronAPI } from '#~/electron/index.js';
// Per rule: use-canonical-logger-not-console (ai.knowledge/platform/studio.client).
import { logger } from '#~/logger/index.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { useLastRoomIdPref } from '#~/room/hook_last_id.js';
import { Paths } from '#~/routes/index.js';
import type { Hierarchy, StudioDescriptor } from '#~/routes/scaffold/header/switcher/types.js';
import { buildRoles, toData } from '#~/routes/scaffold/header/switcher/data.js';
import {
  mergeAuthoritativeBootstrapOrganizations,
  mergeAuthoritativeBootstrapRooms
} from '#~/routes/root/authoritative-bootstrap-inventory.js';
import { Sel, STagNew } from '#~/stag/index.js';
import {
  readAccountStudioPickerState,
  toAccountStudioPickerState,
  type AccountStudioPickerRoom
} from './bootstrap-state.js';
import {
  createAndLinkAccountStudio,
  decideAccountStudioSelection,
  linkAndVerifyAccountStudio,
  readPendingAccountStudioCreate,
  reconcilePendingAccountStudioCreate,
  retirePendingAccountStudioCreateForRoom
} from './account-studio-operations.js';
import { startPickerBridgeLinkFunnel } from './bridge-link-funnel.js';
import { permissionLevelLabel } from './permission-level.js';
import { StudioPicker } from './studio-picker.jsx';
import type { StudioPickerDescriptor } from './types.js';

const PERMISSION_MESSAGE = 'You don’t have permission to link a Bridge to this Studio.';
const CREATE_PERMISSION_MESSAGE = 'You don’t have permission to create a Studio.';
// Setup rollback: how the picker follows a `reserved` reconcile verdict. The
// server refuses the conditional delete while a provisional Bridge row (a
// link handshake mid-flight) holds the Studio. That handshake is bounded on
// the server — linkV2 gives the Bridge `provisionalLinkWorkTimeout` (20s) to
// appear, then rolls the reservation back inside its 29s Lambda budget — so
// the state settles on its own and the picker re-asks on a cadence that
// covers that whole window: 7 re-checks 5s apart = 35s after the first
// refusal. Past the limit the record stays (an `unresolved`-shaped outcome)
// and the next open / create / `online` reconcile resumes it, exactly as
// before. Each re-check is one idempotent rooms.delete under the server's
// row lock, so the cadence costs at most a handful of cheap calls.
// Per rules: settle-every-terminal-branch, absent-data-gates-permissively,
// reference-rules-in-comments (ai.knowledge/global).
const RESERVED_STUDIO_RECHECK_MS = 5_000;
const RESERVED_STUDIO_RECHECK_LIMIT = 7;
// Shown when the Studio the user selected was the one an interrupted create
// left behind, and the pre-link cleanup removed it (product decision: a
// created Studio is removed unless its create-and-link completes).
const REMOVED_BEFORE_LINK_MESSAGE =
  'That studio was removed because its setup never finished. Choose another studio or create a new one.';

// What a reconcile did, for the mutation queued behind it: which Studio the
// pending record named (if any) and how the cleanup ended.
interface ReconcileSettled {
  readonly roomId: Room['id'] | undefined;
  readonly outcome: Awaited<ReturnType<typeof reconcilePendingAccountStudioCreate>>;
}

type ReplacementTarget = AccountStudioPickerRoom;
type MultiBridgeTarget = AccountStudioPickerRoom;
type SetupLinkPolicy = 'require_available' | 'replace_existing' | 'add_existing';

const linkAction = (policy: SetupLinkPolicy): 'add' | 'link' | 'replace' =>
  policy === 'add_existing' ? 'add' : policy === 'replace_existing' ? 'replace' : 'link';

type SetupNotice =
  | { readonly kind: 'permission'; readonly studioName: string; readonly roleLabel?: string }
  | {
      readonly kind: 'link_failed';
      readonly studioName: string;
      readonly roomId: Room['id'];
      readonly policy: SetupLinkPolicy;
    }
  | {
      // The service refused an add as unavailable. Explains; nothing changed.
      readonly kind: 'add_unavailable';
      readonly studioName: string;
      readonly machineNames: readonly string[];
    };

const currentTimeLabel = (): string =>
  new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(new Date());

const networkSettingsUrl = (): string | undefined => {
  const platform = navigator.userAgent.toLowerCase();
  if (platform.includes('windows')) return 'ms-settings:network-status';
  if (platform.includes('mac os')) {
    return 'x-apple.systempreferences:com.apple.preference.network';
  }
  return undefined;
};

const toDescriptors = (
  data: StudioDescriptor[] | Hierarchy,
  rooms: Readonly<Record<RoomRecord['id'], RoomRecord>>,
  inventory: readonly AccountStudioPickerRoom[],
  localAgentId: string | undefined,
  addSupported: boolean
): StudioPickerDescriptor[] => {
  const metadataByRoom = new Map(inventory.map((room) => [room.roomId, room]));
  const applyMetadata = (descriptor: StudioPickerDescriptor): StudioPickerDescriptor => {
    const metadata = metadataByRoom.get(descriptor.room.id);
    if (metadata === undefined) return descriptor;
    const localBridge = metadata.activeBridges.find((bridge) => bridge.id === localAgentId);
    const firstBridge = localBridge ?? metadata.activeBridges[0];
    const isThisDevice = localBridge !== undefined;
    const bridgeStatus: StudioPickerDescriptor['bridgeStatus'] =
      metadata.activeBridges.length === 0
        ? { kind: 'available' }
        : metadata.activeBridges.length === 1 && firstBridge !== undefined
          ? {
              kind: 'linked',
              machineName: firstBridge.name,
              isThisDevice
            }
          : {
              kind: 'multibridge',
              machineNames: metadata.activeBridges.map((bridge) => bridge.name),
              isThisDevice
            };
    return {
      ...descriptor,
      canLinkBridge: metadata.canLinkBridge,
      // Capacity is service-owned. While adding is gated the card keeps its
      // truthful Linked / Multiple bridges status; once supported, absence is a
      // fail-closed Unknown.
      desktopBridgeCapacity:
        addSupported && metadata.activeBridges.length > 0
          ? (metadata.desktopBridgeCapacity ?? { kind: 'unknown' })
          : undefined,
      deviceCount: metadata.activeBridges.length,
      isThisDevice,
      bridgeStatus
    };
  };

  // Roles come from each room's own permissions, so cards keep them before org metadata loads.
  const knownDescriptors: StudioPickerDescriptor[] =
    data.length > 0 && data[0] !== undefined && 'room' in data[0]
      ? (data as StudioDescriptor[]).map(applyMetadata)
      : (data as Hierarchy).flatMap((entry) =>
          entry.rooms.map((room) =>
            applyMetadata({
              badges: [],
              organization: entry.organization,
              room,
              status: 'offline' as const,
              deviceCount: Object.keys(room.agents).length,
              isThisDevice: false,
              roles: buildRoles(room)
            })
          )
        );
  const knownRoomIds = new Set(knownDescriptors.map((descriptor) => descriptor.room.id));

  // Organization metadata is retained when present but never gates the flat picker.
  const roomsWithoutOrganization = Object.values(rooms)
    .filter((room) => !knownRoomIds.has(room.id))
    .map((room) =>
      applyMetadata({
        badges: [],
        room,
        status: 'offline' as const,
        deviceCount: Object.keys(room.agents).length,
        isThisDevice: localAgentId !== undefined && localAgentId in room.agents,
        roles: buildRoles(room)
      })
    );

  return [...knownDescriptors, ...roomsWithoutOrganization];
};

interface PickerErrorBoundaryProps {
  readonly children: ReactNode;
  readonly onError: (error: Error) => void;
}

class PickerErrorBoundary extends Component<PickerErrorBoundaryProps, { failed: boolean }> {
  public override state = { failed: false };

  public static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  public override componentDidCatch(error: Error, _info: ErrorInfo): void {
    this.props.onError(error);
  }

  public override render(): ReactNode {
    if (this.state.failed) {
      return (
        <div className='flex min-h-screen items-center justify-center bg-dark p-24 text-light'>
          <p role='alert'>The Studio picker could not be rendered. Reload Iris to try again.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Post-login account-first Studio picker route. */
export const SelectStudioRoute = (): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessTokenSilently, user: auth0User } = useAuth0();
  const { track } = useAnalytics();
  const { enabled: multiBridgeFlagEnabled } = useFeatureFlag(
    PostHogFeatureFlag.MULTIBRIDGE_FEATURE,
    { defaultValue: false }
  );
  const multiBridgeEnabled = multiBridgeFlagEnabled === true;
  const { enabled: n3Enabled } = useFeatureFlag('alert_n3_enabled', { defaultValue: true });
  const studioListFailed =
    useAuthStore(
      (state) =>
        state.registryLists.studios?.failed === true && state.registryLists.studios.eligible
    ) && n3Enabled === true;
  const analytics = useMemo(() => createStudioBootstrapTracker(track), [track]);
  const bridgeLinkTracker = useMemo(() => createBridgeLifecycleTracker(track), [track]);
  const [rooms, orgs, agents, synchronizer, studioUser, produce] = useDepot(
    (state) => [
      state.roomRecords,
      state.organizations,
      state.agents,
      state.synchronizer,
      state.user,
      state.produce
    ],
    shallow
  );
  const [, setLastRoomId] = useLastRoomIdPref();
  // The name this computer's Bridge registers; every picker surface shows it.
  const { friendlyName: computerName } = useMachineNames();
  const initialState = useMemo(
    () => readAccountStudioPickerState(location.state),
    [location.state]
  );
  // A direct visit has no resolved local-Bridge identity, so it stays selection-only.
  const mode = initialState?.mode ?? 'studio_access';
  const localAgentId = initialState?.localAgentId;
  const [inventory, setInventory] = useState<readonly AccountStudioPickerRoom[]>(
    initialState?.rooms ?? []
  );
  // ENG-4483 W3: server capability advertisement. Seeded from the bootstrap
  // state and refreshed with every primary inventory read; undefined until a
  // primary read lands, which the selection decision treats as unsupported.
  const [capabilities, setCapabilities] = useState<BridgeSetupCapabilities | undefined>(
    initialState?.capabilities
  );
  const desktopAddSupported = capabilities?.desktopAdd === true;
  // Adding to an occupied Studio needs BOTH the client rollout flag and the
  // server capability; either alone leaves the card truthful but gated.
  const addSupported = multiBridgeEnabled && desktopAddSupported;
  const [busy, setBusy] = useState(false);
  const [busyRoomId, setBusyRoomId] = useState<Room['id']>();
  const [createError, setCreateError] = useState(false);
  const [notice, setNotice] = useState<SetupNotice>();
  const [operationStatus, setOperationStatus] = useState<string>();
  const [operationError, setOperationError] = useState<string>();
  const [replacement, setReplacement] = useState<ReplacementTarget>();
  const [multiBridge, setMultiBridge] = useState<MultiBridgeTarget>();
  const [offline, setOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false
  );
  const [offlineRetrying, setOfflineRetrying] = useState(false);
  const [capacityRetrying, setCapacityRetrying] = useState(false);
  const [lastOfflineAttemptLabel, setLastOfflineAttemptLabel] = useState(currentTimeLabel);
  const openedTrackedRef = useRef(false);
  const capacityRetryingRoomsRef = useRef(new Set<Room['id']>());
  const capacityLifetimeRef = useRef(new AbortController());
  const bootAttemptId = getBootSnapshot()?.attemptId;
  useLayoutEffect(() => {
    const lifetime = new AbortController();
    capacityLifetimeRef.current = lifetime;
    capacityRetryingRoomsRef.current = new Set();
    setCapacityRetrying(false);
    // Retired picker requests may finish I/O, but must not publish into the next account or attempt.
    return () => lifetime.abort();
  }, [auth0User?.sub, bootAttemptId]);
  // The picker is a decision surface: an idle picker, its confirmation and
  // notice dialogs, and the offline screen all wait on the person, so that
  // time must not spend the launch budget. A link, create or inventory retry in
  // flight is bounded machine work and stays on the clock. The hook also
  // withholds the wait until the registry has loaded (the Loader branch).
  useRenderedBootHumanWait(!busy && !offlineRetrying && !capacityRetrying);

  // IDs tie each row to its Studio and Desktop Bridges. This computer's Bridge
  // id is known before linking only when it was already linked (a reinstall);
  // link outcomes pass the id the link request named.
  const baseProperties = useCallback(
    (
      target: 'available' | 'linked' | 'create' | 'none' = 'none',
      studio?: Pick<AccountStudioPickerRoom, 'roomId'> &
        Partial<Pick<AccountStudioPickerRoom, 'activeBridges'>>,
      agentId: string | undefined = localAgentId
    ) => ({
      surface: 'electron_account_bootstrap' as const,
      picker_mode: mode,
      local_bridge_state: mode === 'bridge_setup' ? ('unlinked' as const) : ('linked' as const),
      studio_count: inventory.length,
      target_link_status: target,
      ...(studio === undefined ? {} : { studio_id: studio.roomId }),
      ...(agentId === undefined ? {} : { agent_id: agentId }),
      ...(studio?.activeBridges === undefined || studio.activeBridges.length === 0
        ? {}
        : { existing_agent_ids: studio.activeBridges.map((bridge) => bridge.id) })
    }),
    [inventory.length, localAgentId, mode]
  );

  const describeStudio = useCallback(
    (roomId: Room['id']): { studioName: string; roleLabel?: string } => {
      const room = rooms[roomId];
      const studioName = room?.name ?? 'this studio';
      // Same source as the cards: the room's own permissions.
      const roleLabel = room === undefined ? undefined : permissionLevelLabel(buildRoles(room));
      return roleLabel === undefined ? { studioName } : { studioName, roleLabel };
    },
    [rooms]
  );

  useEffect(() => {
    if (openedTrackedRef.current) return;
    openedTrackedRef.current = true;
    analytics.track(EVENT_NAMES.STUDIO_PICKER_OPENED, {
      ...baseProperties(),
      action: 'open',
      outcome: 'success'
    });
  }, [analytics, baseProperties]);

  const refreshInventory = useCallback(
    async (
      token: string,
      isCurrent?: () => boolean
    ): Promise<readonly AccountStudioPickerRoom[]> => {
      // Every caller refreshes AFTER a mutation (a Studio created, linked or
      // rolled back), so these reads must cross the mailroom requester's
      // ten-second response cache: served from it, the boot-time inventory
      // comes back and a Studio the cleanup just deleted stays on the grid.
      const failedLists = authStore.getState().registryLists;
      const [primary, replicaRooms, replicaOrganizations] = await Promise.all([
        inspectInitialRoomInventory(token, { fresh: true }),
        listRooms(token, undefined, { fresh: true }),
        listOrganizations(token, { fresh: true })
      ]);
      // Capacity requests can outlive their picker while the registry read is pending.
      if (isCurrent?.() === false) return [];
      const state = toAccountStudioPickerState(
        mode,
        primary.rooms,
        localAgentId,
        primary.capabilities
      );
      setInventory(state.rooms);
      setCapabilities(primary.capabilities);
      const mergedRooms = mergeAuthoritativeBootstrapRooms(replicaRooms, primary.rooms);
      const mergedOrganizations = mergeAuthoritativeBootstrapOrganizations(
        replicaOrganizations,
        primary.ownedOrganizations
      );
      produce((draft) => {
        draft.roomRecords = toRoomRecordMap(mergedRooms);
        draft.organizations = toOrganizationMap(mergedOrganizations, studioUser);
        draft.synchronizer.hasRegistryLoaded = true;
        draft.synchronizer.isRegistryLoading = false;
      });
      for (const list of ['studios', 'organizations'] as const) {
        // Only resolve the load we refreshed, never a replacement account/run.
        if (authStore.getState().registryLists[list]?.retry === failedLists[list]?.retry)
          authStore.getState().resolveRegistryList(list);
      }
      return state.rooms;
    },
    [localAgentId, mode, produce, studioUser]
  );

  const enterStudio = useCallback(
    (roomId: Room['id'], expectedLocalAgentId?: Agent['id']): void => {
      setLastRoomId(roomId);
      const ownsTarget =
        expectedLocalAgentId !== undefined ||
        inventory
          .find((room) => room.roomId === roomId)
          ?.activeBridges.some((bridge) => bridge.id === localAgentId) === true;
      const path = ownsTarget ? Paths.room.boot.resolve(roomId) : Paths.room.resolve(roomId);
      navigate(`${path}${window.location.search}`);
    },
    [inventory, localAgentId, navigate, setLastRoomId]
  );

  const handleLinkFailure = useCallback(
    async (
      cause: unknown,
      token: string | undefined,
      target?: AccountStudioPickerRoom,
      policy?: SetupLinkPolicy
    ): Promise<void> => {
      const code = apiCode(cause);
      const type = setupErrorType(cause);
      if (type === 'network') {
        setLastOfflineAttemptLabel(currentTimeLabel());
        setOffline(true);
        setNotice(undefined);
        setOperationError(undefined);
        return;
      }
      if (token !== undefined) {
        try {
          await refreshInventory(token);
        } catch (refreshCause) {
          console.warn('Unable to refresh Studio inventory after link failure.', refreshCause);
        }
      }
      if (mode === 'bridge_setup' && target !== undefined) {
        const described = describeStudio(target.roomId);
        if (type === 'permission_denied') {
          setNotice({ kind: 'permission', ...described });
          setOperationError(undefined);
          return;
        }
        // Service refusals with a named reason are explanations, not link
        // failures: the Studio was never touched and "try again" would be wrong.
        if (type === 'capability_unavailable') {
          setNotice({
            kind: 'add_unavailable',
            studioName: described.studioName,
            machineNames: target.activeBridges.map((bridge) => bridge.name)
          });
          setOperationError(undefined);
          return;
        }
        if (type === 'capacity_exhausted') {
          setOperationError('This Studio is at its Desktop Bridge capacity.');
          return;
        }
        if (type === 'capacity_unknown') {
          setOperationError('Bridge count unavailable. Retry before adding this Bridge.');
          return;
        }
        if (type === 'inventory_conflict') {
          setOperationError(
            'This Studio’s Bridge status changed. Review the refreshed list and try again.'
          );
          return;
        }
        setNotice({
          kind: 'link_failed',
          studioName: described.studioName,
          roomId: target.roomId,
          policy: policy ?? 'require_available'
        });
        setOperationError(undefined);
        return;
      }
      setOperationError(
        code === 403
          ? PERMISSION_MESSAGE
          : type === 'inventory_conflict'
            ? 'This Studio’s Bridge status changed. Review the refreshed list and try again.'
            : 'Iris could not link this Bridge. Review the Studio status and try again.'
      );
    },
    [describeStudio, mode, refreshInventory]
  );

  // Setup rollback: settle a create that a previous session (or an earlier failure in
  // this one) left half-finished. The whole task — authenticating included —
  // is registered on the ref SYNCHRONOUSLY, before anything awaits, so a
  // Create or link that starts while it is still authenticating waits for it
  // rather than racing a cleanup that may delete the very Studio it adopts.
  const reconcileRef = useRef<Promise<void>>(Promise.resolve());
  const reconciledAccountsRef = useRef(new Set<string>());
  // The mutation currently running (Create or link), if any. A reconcile that
  // starts mid-mutation — the `online` event can fire while a create-and-link
  // is in flight — waits for it, otherwise it would read that attempt's own
  // pending record and delete the Studio it is about to link. Mutations
  // register here SYNCHRONOUSLY before awaiting the reconcile barrier, and
  // the reconcile snapshots the value before its first await, so the two
  // barriers never wait on each other.
  const mutationRef = useRef<Promise<void>>(Promise.resolve());
  const beginMutation = useCallback((): (() => void) => {
    const { promise, resolve } = Promise.withResolvers<void>();
    mutationRef.current = promise;
    return resolve;
  }, []);
  // A `reserved` verdict schedules ONE pending re-check (never a second while
  // one is armed) and counts attempts; any other verdict resets the count.
  // The re-check runs the same reconcile, so it queues behind any mutation in
  // flight like every other reconcile does. The latest reconcile function is
  // reached through a ref because the scheduling happens inside the reconcile
  // itself (a useCallback cannot name itself).
  const reservedRecheckTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const reservedRechecksRef = useRef(0);
  const reconcileAgainRef = useRef<() => void>(() => undefined);
  const followReservedStudio = useCallback((roomId: Room['id'] | undefined): void => {
    if (reservedRecheckTimerRef.current !== undefined) return;
    if (reservedRechecksRef.current >= RESERVED_STUDIO_RECHECK_LIMIT) {
      // Something other than our own escaped handshake holds the Studio for
      // longer than the server's handshake budget. Stop re-asking; the record
      // stays for the next open / create / `online` reconcile.
      logger.warning(
        'Studio stayed reserved past the link handshake budget; leaving its record for a later reconcile.',
        { roomId }
      );
      return;
    }
    reservedRechecksRef.current += 1;
    reservedRecheckTimerRef.current = setTimeout(() => {
      reservedRecheckTimerRef.current = undefined;
      reconcileAgainRef.current();
    }, RESERVED_STUDIO_RECHECK_MS);
  }, []);
  useEffect(
    () => () => {
      if (reservedRecheckTimerRef.current !== undefined) {
        clearTimeout(reservedRecheckTimerRef.current);
        reservedRecheckTimerRef.current = undefined;
      }
    },
    []
  );
  const reconcilePendingCreate = useCallback((): Promise<ReconcileSettled | undefined> => {
    // Snapshot both barriers NOW, then chain: reconciles run one at a time in
    // scheduling order, and the ref always points at the LAST of them, so a
    // mutation that awaits it waits for every outstanding cleanup — never
    // only the newest while an older one is still authenticating.
    const activeMutation = mutationRef.current;
    const previousReconcile = reconcileRef.current;
    const run = (async (): Promise<ReconcileSettled | undefined> => {
      await previousReconcile;
      await activeMutation;
      try {
        const pendingRoomId = (await readPendingAccountStudioCreate(auth0User?.sub))?.roomId;
        // Create and link wait on this task, so its prerequisites must be
        // bounded the same way theirs are: a stalled silent auth ends the
        // reconcile (record kept) instead of holding the picker busy. The
        // cloud calls that follow are bounded by the mailroom request
        // timeout; nothing here can still be running once this settles.
        const token = await withStudioOperationDeadline(
          () => getAccessTokenSilently(),
          'authenticating for setup cleanup'
        );
        const outcome = await reconcilePendingAccountStudioCreate({
          accountId: auth0User?.sub,
          expectedAgentId: localAgentId as Agent['id'] | undefined,
          token
        });
        // Only a changed inventory needs re-reading; the initial read already ran.
        if (outcome === 'deleted' || outcome === 'missing') {
          await refreshInventory(token);
        }
        // A reservation is transient server state, not a verdict: ask again
        // once the handshake has had time to settle (see the constants above).
        if (outcome === 'reserved') {
          followReservedStudio(pendingRoomId);
        } else {
          reservedRechecksRef.current = 0;
        }
        return { roomId: pendingRoomId, outcome };
      } catch (cause) {
        // Non-fatal by design: the record persists and the next open retries.
        logger.warning('Unable to reconcile an interrupted Studio create.', cause);
        return undefined;
      }
    })();
    reconcileRef.current = run.then(() => undefined);
    return run;
  }, [
    auth0User?.sub,
    followReservedStudio,
    getAccessTokenSilently,
    localAgentId,
    refreshInventory
  ]);
  useEffect(() => {
    reconcileAgainRef.current = (): void => {
      void reconcilePendingCreate();
    };
  }, [reconcilePendingCreate]);

  useEffect(() => {
    // Same gate as handleCreate: only surfaces that can create can have left an orphan.
    if (mode === 'studio_access' && localAgentId === undefined) return;
    const accountKey = auth0User?.sub ?? 'unknown';
    if (reconciledAccountsRef.current.has(accountKey)) return;
    reconciledAccountsRef.current.add(accountKey);
    void reconcilePendingCreate();
  }, [auth0User?.sub, localAgentId, mode, reconcilePendingCreate]);

  const linkRoom = useCallback(
    async (target: AccountStudioPickerRoom, policy: SetupLinkPolicy) => {
      // A fresh reconcile, scheduled BEFORE this mutation registers so it
      // snapshots the previous (settled) mutation and cannot wait on us. The
      // one already on the ref may have given up (Stripe cancel failed, record
      // kept, Studio still marked); awaiting only that settled promise would
      // let this link hit the marked Studio and be refused every time.
      const reconcile = reconcilePendingCreate();
      const settleMutation = beginMutation();
      setBusy(true);
      setBusyRoomId(target.roomId);
      setCreateError(false);
      setNotice(undefined);
      setMultiBridge(undefined);
      setReplacement(undefined);
      setOperationStatus('Connecting bridge…');
      setOperationError(undefined);
      const bridgeLink = startPickerBridgeLinkFunnel(bridgeLinkTracker, {
        action: linkAction(policy),
        pickerMode: mode
      });
      let token: string | undefined;
      try {
        // A pending-create card is selectable; let the reconcile settle it first.
        const settled = await reconcile;
        if (
          settled?.roomId === target.roomId &&
          (settled.outcome === 'deleted' || settled.outcome === 'missing')
        ) {
          // The card WAS the interrupted create's Studio and the cleanup just
          // removed it. Linking the deleted id would only
          // fail; the reconcile already refreshed the grid, so explain and stop.
          analytics.track(EVENT_NAMES.STUDIO_LINK_FAILED, {
            ...baseProperties(target.activeBridges.length === 0 ? 'available' : 'linked', target),
            action: linkAction(policy),
            outcome: 'conflict',
            error_type: 'inventory_conflict'
          });
          setOperationError(REMOVED_BEFORE_LINK_MESSAGE);
          setBusy(false);
          setBusyRoomId(undefined);
          setOperationStatus(undefined);
          return;
        }
        token = await withStudioOperationDeadline(() => getAccessTokenSilently(), 'authenticating');
        const { agentId } = await linkAndVerifyAccountStudio({
          expectedAgentId: localAgentId as Agent['id'] | undefined,
          // The consent snapshot: exactly the committed Bridges shown on the
          // card and named in the confirmation. The server refuses if it moved.
          expectedDisplacedAgentIds:
            policy === 'replace_existing'
              ? target.activeBridges.map((bridge) => bridge.id as Agent['id'])
              : undefined,
          expectedExistingAgentIds:
            policy === 'add_existing'
              ? target.activeBridges.map((bridge) => bridge.id as Agent['id'])
              : undefined,
          onLinkAttempt: bridgeLink.onLinkAttempt,
          policy,
          roomId: target.roomId,
          token
        });
        // Settle on the verified link itself, so a later bookkeeping failure
        // below cannot turn this link into a bridge_link_failed row.
        bridgeLink.succeeded();
        // The card the user linked may be the Studio an earlier create left
        // behind; a verified link makes it theirs, so its rollback record must
        // not survive to delete it later.
        await retirePendingAccountStudioCreateForRoom(auth0User?.sub, target.roomId);
        try {
          await refreshInventory(token);
        } catch (cause) {
          // The verified relationship wins over a lagging replica refresh.
          logger.warning('Unable to refresh Studio inventory after a verified link.', cause);
        }
        const linked = {
          ...baseProperties(
            target.activeBridges.length === 0 ? 'available' : 'linked',
            target,
            agentId
          ),
          action: linkAction(policy),
          outcome: 'success' as const
        };
        analytics.track(EVENT_NAMES.STUDIO_LINK_SUCCEEDED, linked);
        analytics.track(EVENT_NAMES.STUDIO_SETUP_SUCCEEDED, linked);
        enterStudio(target.roomId, agentId);
      } catch (cause) {
        bridgeLink.failed(cause);
        const failureType = setupErrorType(cause);
        analytics.track(EVENT_NAMES.STUDIO_LINK_FAILED, {
          ...baseProperties(
            target.activeBridges.length === 0 ? 'available' : 'linked',
            target,
            bridgeLink.attemptedAgentId() ?? localAgentId
          ),
          action: linkAction(policy),
          outcome: failureType === 'inventory_conflict' ? 'conflict' : 'failure',
          error_type: failureType
        });
        await handleLinkFailure(cause, token, target, policy);
        setBusy(false);
        setBusyRoomId(undefined);
        setOperationStatus(undefined);
      } finally {
        settleMutation();
      }
    },
    [
      analytics,
      auth0User?.sub,
      baseProperties,
      beginMutation,
      bridgeLinkTracker,
      enterStudio,
      getAccessTokenSilently,
      handleLinkFailure,
      localAgentId,
      mode,
      reconcilePendingCreate,
      refreshInventory
    ]
  );

  const openReplacement = useCallback(
    (target: ReplacementTarget): void => {
      setOperationError(undefined);
      setReplacement(target);
      analytics.track(EVENT_NAMES.STUDIO_REPLACEMENT_CONFIRMATION_OPENED, {
        ...baseProperties('linked', target),
        action: 'replace',
        outcome: 'started'
      });
    },
    [analytics, baseProperties]
  );

  const handleSelect = useCallback(
    (roomId: Room['id']): void => {
      setCreateError(false);
      setNotice(undefined);
      const target = inventory.find((room) => room.roomId === roomId);
      const targetStatus =
        target === undefined ? 'none' : target.activeBridges.length === 0 ? 'available' : 'linked';
      // Direct browser access has depot rooms but no Electron bootstrap inventory.
      if (mode === 'studio_access' && initialState === undefined && target === undefined) {
        analytics.track(EVENT_NAMES.STUDIO_SELECTED, {
          ...baseProperties(targetStatus, { roomId }),
          action: 'open',
          outcome: 'started'
        });
        enterStudio(roomId);
        return;
      }
      const decision = decideAccountStudioSelection(
        mode,
        target,
        localAgentId as Agent['id'] | undefined,
        { multiBridgeEnabled, desktopAddSupported }
      );
      analytics.track(EVENT_NAMES.STUDIO_SELECTED, {
        ...baseProperties(targetStatus, target ?? { roomId }),
        action: decision.kind === 'open' ? 'open' : 'link',
        outcome: 'started'
      });

      switch (decision.kind) {
        case 'open':
          enterStudio(roomId);
          return;
        case 'missing':
          setOperationError('This Studio is no longer available. Reload Iris to refresh the list.');
          return;
        case 'denied': {
          if (mode === 'bridge_setup') {
            setNotice({ kind: 'permission', ...describeStudio(roomId) });
            setOperationError(undefined);
          } else {
            setOperationError(PERMISSION_MESSAGE);
          }
          analytics.track(EVENT_NAMES.STUDIO_LINK_PERMISSION_DENIED, {
            ...baseProperties(targetStatus, target ?? { roomId }),
            action: 'link',
            outcome: 'denied',
            error_type: 'permission_denied'
          });
          return;
        }
        case 'capacity_unknown':
          setOperationError('Bridge count unavailable. Retry before adding this Bridge.');
          return;
        case 'at_capacity':
          setOperationError('This Studio is at its Desktop Bridge capacity.');
          return;
        case 'link_available':
          void linkRoom(decision.target, 'require_available');
          return;
        case 'confirm_add':
          setOperationError(undefined);
          setMultiBridge(decision.target);
          analytics.track(EVENT_NAMES.STUDIO_MULTIBRIDGE_CONFIRMATION_OPENED, {
            ...baseProperties('linked', decision.target),
            action: 'add',
            outcome: 'started'
          });
          return;
        case 'confirm_replace':
          openReplacement(decision.target);
      }
    },
    [
      analytics,
      baseProperties,
      describeStudio,
      enterStudio,
      initialState,
      inventory,
      linkRoom,
      localAgentId,
      multiBridgeEnabled,
      desktopAddSupported,
      mode,
      openReplacement
    ]
  );

  const handleReplacementCancel = useCallback(
    (_reason: 'cancel' | 'close' | 'backdrop' | 'escape'): void => {
      setReplacement(undefined);
      analytics.track(EVENT_NAMES.STUDIO_REPLACEMENT_CONFIRMATION_CANCELLED, {
        ...baseProperties('linked', replacement),
        action: 'replace',
        outcome: 'cancelled'
      });
    },
    [analytics, baseProperties, replacement]
  );

  const handleReplacementConfirm = useCallback((): void => {
    const target = replacement;
    if (target === undefined) return;
    analytics.track(EVENT_NAMES.STUDIO_REPLACEMENT_CONFIRMATION_CONFIRMED, {
      ...baseProperties('linked', target),
      action: 'replace',
      outcome: 'started'
    });
    void linkRoom(target, 'replace_existing');
  }, [analytics, baseProperties, linkRoom, replacement]);

  const handleMultiBridgeCancel = useCallback(
    (_reason: 'cancel' | 'backdrop' | 'escape'): void => {
      setMultiBridge(undefined);
      analytics.track(EVENT_NAMES.STUDIO_MULTIBRIDGE_CONFIRMATION_CANCELLED, {
        ...baseProperties('linked', multiBridge),
        action: 'add',
        outcome: 'cancelled'
      });
    },
    [analytics, baseProperties, multiBridge]
  );

  const handleMultiBridgeConfirm = useCallback((): void => {
    const target = multiBridge;
    if (target === undefined) return;
    analytics.track(EVENT_NAMES.STUDIO_MULTIBRIDGE_CONFIRMATION_CONFIRMED, {
      ...baseProperties('linked', target),
      action: 'add',
      outcome: 'started'
    });
    // ENG-4483 W3: confirmation is consent to ADD this computer alongside the
    // named Bridges. The server re-checks that exact set at begin and finalize.
    void linkRoom(target, 'add_existing');
  }, [analytics, baseProperties, linkRoom, multiBridge]);

  const handleCreate = useCallback((): void => {
    if (busy || (mode === 'studio_access' && localAgentId === undefined)) return;
    void (async () => {
      // Try again after an unresolved cleanup: the
      // reconcile on the ref has settled and given up, the record and the
      // server-side marker remain, and the same key would resolve to that
      // marked Studio — which the server refuses (409) until the rollback
      // finishes. So every Create runs a fresh reconcile first. Scheduled
      // BEFORE beginMutation so it snapshots the previous mutation, not ours.
      const reconcile = reconcilePendingCreate();
      const settleMutation = beginMutation();
      setBusy(true);
      setBusyRoomId(undefined);
      setCreateError(false);
      setNotice(undefined);
      setOperationStatus('Creating studio…');
      setOperationError(undefined);
      analytics.track(EVENT_NAMES.STUDIO_CREATE_REQUESTED, {
        ...baseProperties('create'),
        action: 'create',
        outcome: 'started'
      });
      const bridgeLink = startPickerBridgeLinkFunnel(bridgeLinkTracker, {
        action: 'create',
        pickerMode: mode
      });
      let token: string | undefined;
      let createdRoomId: Room['id'] | undefined;
      // Set when the post-create rollback removed the new Studio (or found it
      // already gone): the user is then back where they started, so the UI
      // reports a create failure with Try again, not a dangling link failure.
      let rolledBack = false;
      try {
        await reconcile;
        token = await withStudioOperationDeadline(() => getAccessTokenSilently(), 'authenticating');
        const created = await createAndLinkAccountStudio({
          accountId: auth0User?.sub,
          expectedAgentId: localAgentId as Agent['id'] | undefined,
          token,
          onCreated: (result): void => {
            createdRoomId = result.roomId;
            setInventory(toAccountStudioPickerState(mode, result.rooms, localAgentId).rooms);
            analytics.track(EVENT_NAMES.STUDIO_CREATED_FROM_PICKER, {
              ...baseProperties('create', { roomId: result.roomId }),
              action: 'create',
              outcome: 'success'
            });
          },
          onLinkFailed: (outcome): void => {
            rolledBack = outcome === 'deleted' || outcome === 'missing';
          },
          onLinkAttempt: bridgeLink.onLinkAttempt
        });
        bridgeLink.succeeded();
        try {
          await refreshInventory(token);
        } catch (cause) {
          // The verified relationship wins over a lagging replica refresh.
          logger.warning('Unable to refresh Studio inventory after a verified create.', cause);
        }
        // Creation and linking have separate terminals: report link success only
        // after createAndLinkAccountStudio has verified the committed relationship.
        const linked = {
          ...baseProperties('create', { roomId: created.roomId }, created.agentId),
          action: 'create' as const,
          outcome: 'success' as const
        };
        analytics.track(EVENT_NAMES.STUDIO_LINK_SUCCEEDED, linked);
        analytics.track(EVENT_NAMES.STUDIO_SETUP_SUCCEEDED, linked);
        enterStudio(created.roomId, created.agentId);
      } catch (cause) {
        bridgeLink.failed(cause);
        analytics.track(
          createdRoomId === undefined
            ? EVENT_NAMES.STUDIO_CREATE_FAILED
            : EVENT_NAMES.STUDIO_LINK_FAILED,
          {
            ...baseProperties(
              'create',
              createdRoomId === undefined ? undefined : { roomId: createdRoomId },
              bridgeLink.attemptedAgentId() ?? localAgentId
            ),
            action: createdRoomId === undefined ? 'create' : 'link',
            outcome: apiCode(cause) === 409 ? 'conflict' : 'failure',
            error_type: setupErrorType(cause)
          }
        );
        if (setupErrorType(cause) === 'network') {
          await handleLinkFailure(cause, token);
        } else if (createdRoomId === undefined || rolledBack) {
          // QA-guide contract: "Couldn't create the studio." with Try again, and
          // no Studio left behind. A rolled-back link failure lands here too.
          setCreateError(true);
          setOperationError(
            createdRoomId === undefined && apiCode(cause) === 403
              ? CREATE_PERMISSION_MESSAGE
              : "Couldn't create the studio."
          );
          if (rolledBack && token !== undefined) {
            try {
              await refreshInventory(token);
            } catch (refreshCause) {
              logger.warning('Unable to refresh Studio inventory after a rollback.', refreshCause);
            }
          }
        } else {
          await handleLinkFailure(cause, token);
        }
        setBusy(false);
        setBusyRoomId(undefined);
        setOperationStatus(undefined);
      } finally {
        settleMutation();
      }
    })();
  }, [
    analytics,
    auth0User?.sub,
    baseProperties,
    beginMutation,
    bridgeLinkTracker,
    busy,
    enterStudio,
    getAccessTokenSilently,
    handleLinkFailure,
    localAgentId,
    mode,
    reconcilePendingCreate,
    refreshInventory
  ]);

  const retryCapacity = useCallback(
    (roomId: Room['id']): void => {
      const retryingRooms = capacityRetryingRoomsRef.current;
      if (retryingRooms.has(roomId)) return;
      retryingRooms.add(roomId);
      const lifetime = capacityLifetimeRef.current;
      const attemptId = getBootSnapshot()?.attemptId;
      const isCurrent = (): boolean => {
        const snapshot = getBootSnapshot();
        return (
          !lifetime.signal.aborted &&
          capacityLifetimeRef.current === lifetime &&
          snapshot?.attemptId === attemptId &&
          snapshot?.outcome !== 'failed' &&
          snapshot?.outcome !== 'cancelled'
        );
      };
      // Authentication is machine work too; resume the deadline before awaiting it.
      setCapacityRetrying(true);
      void (async () => {
        // Capacity refresh is non-mutating, so it must not render Bridge-link progress.
        setOperationError(undefined);
        try {
          const token = await getAccessTokenSilently();
          if (!isCurrent()) return;
          await refreshInventory(token, isCurrent);
        } catch (cause) {
          if (!isCurrent()) return;
          if (setupErrorType(cause) === 'network') {
            setLastOfflineAttemptLabel(currentTimeLabel());
            setOffline(true);
          } else {
            setOperationError('Bridge capacity is still unavailable. Try again.');
          }
        } finally {
          retryingRooms.delete(roomId);
          // Per rule: settle-every-terminal-branch (ai.knowledge/global), keep concurrent retries on the clock.
          if (!lifetime.signal.aborted && capacityLifetimeRef.current === lifetime) {
            setCapacityRetrying(retryingRooms.size > 0);
          }
        }
      })();
    },
    [getAccessTokenSilently, refreshInventory]
  );

  const retryOffline = useCallback((): void => {
    if (offlineRetrying) return;
    void (async () => {
      setOfflineRetrying(true);
      setLastOfflineAttemptLabel(currentTimeLabel());
      try {
        const token = await getAccessTokenSilently();
        await refreshInventory(token);
        setOffline(false);
        // A network failure mid-create leaves its rollback unresolved; now that
        // the service is reachable again, settle it before the user acts.
        await reconcilePendingCreate();
      } catch {
        setOffline(true);
      } finally {
        setOfflineRetrying(false);
      }
    })();
  }, [getAccessTokenSilently, offlineRetrying, reconcilePendingCreate, refreshInventory]);

  useEffect(() => {
    const markOffline = (): void => {
      setLastOfflineAttemptLabel(currentTimeLabel());
      setOffline(true);
    };
    const retryWhenOnline = (): void => retryOffline();
    window.addEventListener('offline', markOffline);
    window.addEventListener('online', retryWhenOnline);
    return () => {
      window.removeEventListener('offline', markOffline);
      window.removeEventListener('online', retryWhenOnline);
    };
  }, [retryOffline]);

  const settingsUrl = networkSettingsUrl();
  const openNetworkSettings =
    settingsUrl === undefined ? undefined : (): void => ElectronAPI.openUrl(settingsUrl);

  if (mode === 'bridge_setup' && offline) {
    return (
      <StudioPicker
        variant='full-viewport'
        organizationPresentation='hidden'
        pickerMode={mode}
        computerName={computerName}
        descriptors={[]}
        onSelect={handleSelect}
        offline={{
          lastAttemptLabel: lastOfflineAttemptLabel,
          retrying: offlineRetrying,
          onRetry: retryOffline,
          ...(openNetworkSettings === undefined ? {} : { onNetworkSettings: openNetworkSettings })
        }}
      />
    );
  }

  if (!synchronizer.hasRegistryLoaded) return <Loader />;

  const descriptors = toDescriptors(
    toData(rooms, orgs, agents, localAgentId as Agent['id'] | undefined),
    rooms,
    inventory,
    localAgentId,
    addSupported
  );

  if (descriptors.length === 0 && !studioListFailed) {
    return (
      <div
        className='flex min-h-screen flex-col items-center justify-center gap-4 bg-dark text-light'
        {...STagNew(Sel.StudioPicker, 'empty_state')}
      >
        <img
          src={getCurrentVendorBadgeIcon()}
          alt={`${vendorName()} logo`}
          className='size-40 object-contain'
        />
        <h1 className='text-[22px] font-demibold'>No studios yet</h1>
        <p className='max-w-[380px] text-center text-[14px] text-muted'>
          Set up your first studio to start using {vendorName()}.
        </p>
        <Anchor
          className='text-primary'
          {...STagNew(Sel.StudioPicker, 'go_to_setup')}
          onClick={(): void => {
            // React Router data navigation may be async; the click handler deliberately stays void.
            void navigate(`/${Paths.agent.setup.$}`);
          }}
        >
          Set up a studio
        </Anchor>
      </div>
    );
  }

  const localRoomId = inventory.find((room) =>
    room.activeBridges.some((bridge) => bridge.id === localAgentId)
  )?.roomId;
  const renderFailed = (error: Error): void => {
    console.error('Studio picker render failed.', error);
    analytics.track(EVENT_NAMES.STUDIO_PICKER_RENDER_FAILED, {
      ...baseProperties(),
      action: 'open',
      outcome: 'failure',
      error_type: 'unknown'
    });
  };

  return (
    <PickerErrorBoundary onError={renderFailed}>
      <StudioPicker
        variant='full-viewport'
        organizationPresentation='hidden'
        pickerMode={mode}
        computerName={computerName}
        descriptors={descriptors}
        currentRoomId={localRoomId}
        busy={busy}
        busyRoomId={busyRoomId}
        createError={createError}
        operationStatus={operationStatus}
        errorMessage={operationError}
        // Studio access is selection-only; creation belongs to Bridge setup.
        onCreate={mode === 'bridge_setup' ? handleCreate : undefined}
        onRetryCapacity={addSupported ? retryCapacity : undefined}
        onSelect={handleSelect}
        multiBridge={
          multiBridge === undefined
            ? undefined
            : {
                studioName: describeStudio(multiBridge.roomId).studioName,
                machineNames: multiBridge.activeBridges.map((bridge) => bridge.name),
                onCancel: handleMultiBridgeCancel,
                onConfirm: handleMultiBridgeConfirm
              }
        }
        replacement={
          replacement === undefined
            ? undefined
            : {
                machineNames: replacement.activeBridges.map((bridge) => bridge.name),
                onCancel: handleReplacementCancel,
                onConfirm: handleReplacementConfirm
              }
        }
        notice={
          notice === undefined
            ? undefined
            : notice.kind === 'permission'
              ? {
                  kind: 'permission',
                  studioName: notice.studioName,
                  roleLabel: notice.roleLabel,
                  onBack: (): void => setNotice(undefined),
                  onCreate: handleCreate
                }
              : notice.kind === 'add_unavailable'
                ? {
                    kind: 'add_unavailable',
                    studioName: notice.studioName,
                    machineNames: notice.machineNames,
                    onBack: (): void => setNotice(undefined),
                    onCreate: handleCreate
                  }
                : {
                    kind: 'link_failed',
                    studioName: notice.studioName,
                    onCancel: (): void => setNotice(undefined),
                    onRetry: (): void => {
                      const target = inventory.find((room) => room.roomId === notice.roomId);
                      if (target === undefined) {
                        setNotice(undefined);
                        setOperationError(
                          'This Studio is no longer available. Reload Iris to refresh the list.'
                        );
                        return;
                      }
                      // A replacement retry goes back through the decision, so
                      // the user confirms again against the latest inventory.
                      if (notice.policy === 'replace_existing') {
                        handleSelect(target.roomId);
                        return;
                      }
                      void linkRoom(target, notice.policy);
                    }
                  }
        }
      />
    </PickerErrorBoundary>
  );
};
import { withStudioOperationDeadline } from './operation-deadline.js';
